import React, { useState } from 'react';
import './reservationdemandes.css';
import { useTranslation } from 'react-i18next'; // Import the translation hook

const ReservationCard = ({ type, details }) => {
  const { t } = useTranslation(); // Initialize the translation hook

  const getStatusClass = (status) => {
    switch(status) {
      case 'PENDING': return 'status-pending';
      case 'APPROVED':
      case 'ACCEPTED': return 'status-approved';
      case 'REJECTED': return 'status-rejected';
      default: return '';
    }
  };

  const getPaymentStatusClass = (isPayed) => {
    return isPayed ? 'status-approved' : 'status-pending';
  };

  const renderCardContent = () => {
    switch (type) {
      case 'VISA':
        return (
          <div className="reservation-card visa-card">
            <h3 className="reservation-card-title">{t("reservationdemandes.visa_reservation")}</h3>
            <div className="reservation-details">
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.country_label")}</span>
                <span className="reservation-detail-value">{details.country}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.visa_type_label")}</span>
                <span className="reservation-detail-value">{details.visaType}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.applicant_label")}</span>
                <span className="reservation-detail-value">{details.applicantName} {details.applicantSurname}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.status_label")}</span>
                <span className={`reservation-status ${getStatusClass(details.status)}`}>
                  {t(`reservationdemandes.${details.status.toLowerCase()}`)}
                </span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.payment_label")}</span>
                <span className={`reservation-status ${getPaymentStatusClass(details.ispayed)}`}>
                  {details.ispayed ? t("reservationdemandes.paid") : t("reservationdemandes.not_paid")}
                </span>
              </div>
            </div>
          </div>
        );
      
      case 'HOTEL':
        return (
          <div className="reservation-card hotel-card">
            <h3 className="reservation-card-title">{t("reservationdemandes.hotel_reservation")}</h3>
            <div className="reservation-details">
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.name_label")}</span>
                <span className="reservation-detail-value">{details.name || details.nom} {details.surnom}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.checkin_label")}</span>
                <span className="reservation-detail-value">
                  {new Date(details.checkinDate || details.checkin_date).toLocaleDateString()}
                </span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.checkout_label")}</span>
                <span className="reservation-detail-value">
                  {new Date(details.checkoutDate || details.checkout_date).toLocaleDateString()}
                </span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.total_nights_label")}</span>
                <span className="reservation-detail-value">{details.totalNights || details.totalNights}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.status_label")}</span>
                <span className={`reservation-status ${getStatusClass(details.status)}`}>
                  {t(`reservationdemandes.${details.status.toLowerCase()}`)}
                </span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.payment_label")}</span>
                <span className={`reservation-status ${getPaymentStatusClass(details.ispayed)}`}>
                  {details.ispayed ? t("reservationdemandes.paid") : t("reservationdemandes.not_paid")}
                </span>
              </div>
            </div>
          </div>
        );
      
      case 'VOYAGE':
        return (
          <div className="reservation-card voyage-card">
            <h3 className="reservation-card-title">{t("reservationdemandes.voyage_reservation")}</h3>
            <div className="reservation-details">
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.name_label")}</span>
                <span className="reservation-detail-value">{details.nom} {details.surnom}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.email_label")}</span>
                <span className="reservation-detail-value">{details.email}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.nationality_label")}</span>
                <span className="reservation-detail-value">{details.nationality}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.adults_label")}</span>
                <span className="reservation-detail-value">{details.n_adult}</span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.status_label")}</span>
                <span className={`reservation-status ${getStatusClass(details.status)}`}>
                  {t(`reservationdemandes.${details.status.toLowerCase()}`)}
                </span>
              </div>
              <div className="reservation-detail">
                <span className="reservation-detail-label">{t("reservationdemandes.payment_label")}</span>
                <span className={`reservation-status ${getPaymentStatusClass(details.ispayed)}`}>
                  {details.ispayed ? t("reservationdemandes.paid") : t("reservationdemandes.not_paid")}
                </span>
              </div>
            </div>
          </div>
        );
      
      default:
        return <div className="reservation-card">Unknown Reservation Type</div>;
    }
  };

  return renderCardContent();
};

export default function Reservationdemandes({ userData }) {
  const { t } = useTranslation(); // Initialize the translation hook

  // Combine all reservations into a single array with type
  const allReservations = userData ? [
    ...(userData.visas || []).map(visa => ({ type: 'VISA', details: visa })),
    ...(userData.hotelReservations || []).map(hotel => ({ type: 'HOTEL', details: hotel })),
    ...(userData.reservations || []).map(voyage => ({ type: 'VOYAGE', details: voyage }))
  ] : [];

  // Filter state
  const [activeFilter, setActiveFilter] = useState('ALL');

  // Apply filter
  const filteredReservations = activeFilter === 'ALL'
    ? allReservations
    : allReservations.filter(r => r.type === activeFilter);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const reservationsPerPage = 4;

  // Reset to page 1 when filter changes
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  // Calculate pagination
  const indexOfLastReservation = currentPage * reservationsPerPage;
  const indexOfFirstReservation = indexOfLastReservation - reservationsPerPage;
  const currentReservations = filteredReservations.slice(indexOfFirstReservation, indexOfLastReservation);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Calculate total pages
  const totalPages = Math.ceil(filteredReservations.length / reservationsPerPage);

  const filters = [
    { key: 'ALL', label: t("reservationdemandes.filter_all") },
    { key: 'VISA', label: t("reservationdemandes.filter_visa") },
    { key: 'HOTEL', label: t("reservationdemandes.filter_hotel") },
    { key: 'VOYAGE', label: t("reservationdemandes.filter_voyage") },
  ];

  return (
    <div className="reservationdemandes-container">
      <h2>{t("reservationdemandes.title")}</h2>

      {allReservations.length > 0 && (
        <div className="reservation-filters">
          {filters.map(f => (
            <button
              key={f.key}
              className={`filter-btn${activeFilter === f.key ? ' active' : ''}`}
              onClick={() => handleFilterChange(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {filteredReservations.length === 0 ? (
        <p className="no-reservations-text">
          {t("reservationdemandes.no_reservations")}
        </p>
      ) : (
        <>
          <div className="reservations-grid">
            {currentReservations.map((reservation, index) => (
              <ReservationCard 
                key={`${reservation.type}-${index}`} 
                type={reservation.type} 
                details={reservation.details} 
              />
            ))}
          </div>

          {/* Pagination — only show if more than 1 page */}
          {totalPages > 1 && (
            <div className="pagination">
              {currentPage > 1 && (
                <button
                  onClick={() => paginate(currentPage - 1)}
                  className="pagination-button"
                >
                  {t("reservationdemandes.previous_button")}
                </button>
              )}

              {[...Array(totalPages)].map((_, index) => (
                <button
                  key={index}
                  onClick={() => paginate(index + 1)}
                  className={`pagination-button ${currentPage === index + 1 ? 'active' : ''}`}
                >
                  {index + 1}
                </button>
              ))}

              {currentPage < totalPages && (
                <button
                  onClick={() => paginate(currentPage + 1)}
                  className="pagination-button"
                >
                  {t("reservationdemandes.next_button")}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}